<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Records">
      <router-link :to="{ name: 'RecordCreate' }" class="btn btn-primary">
        Add Record
      </router-link>
    </PageHeader>

    <div class="container">
      <Loading v-if="loading" />
      <div class="card" v-else>
        <!-- Header -->
        <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
          <div class="flex flex-col space-y-3 md:space-y-0 md:flex-row md:space-x-3">
            <!-- Filter -->
            <filter-dropdown @apply="applyFilter" @clear="clearFilter" :loading="loading">
              <div>
                <label for="date" class="form-label">Date Range</label>
                <DatePicker  color="indigo" mode="dateTime" is-range is24hr :model-config="{ start: { timeAdjust: '00:00:00' }, end: { timeAdjust: '23:59:59' } }" v-model="filter.date">
                  <template v-slot="{ inputValue, inputEvents }">
                    <div class="flex justify-center">
                      <input
                        :value="inputValue.start"
                        v-on="inputEvents.start"
                        class="form-input prepend"
                      />
                      <div class="flex items-center justify-center flex-none w-10 text-gray-500 border-t border-b border-gray-300">
                        &rarr;
                      </div>
                      <input
                        :value="inputValue.end"
                        v-on="inputEvents.end"
                        class="form-input append"
                      />
                    </div>
                  </template>
                </DatePicker>
              </div>
              <div>
                <label for="vendor" class="form-label">Vendor</label>
                <vendor-select type="vendor" v-model="filter.vendor" />
              </div>
              <div>
                <label for="category" class="form-label">Categories</label>
                <category-select v-model="filter.category" />
              </div>
              <div>
                <label for="vehicle" class="form-label">Vehicle</label>
                <vehicle-select v-model="filter.vehicle" />
              </div>
              <div>
                <label for="vehicle_group" class="form-label">Vehicle Group</label>
                <group-select type="vehicle" v-model="filter.vehicle_group" />
              </div>
            </filter-dropdown>
            <!-- Sort -->
            <sort-dropdown :options="sortOptions" />
            <!-- Export -->
            <button class="self-end w-full btn btn-secondary btn-sm" @click="exportRecords" v-if="user.type === 'admin'">
              Export <fa :icon="['far', 'arrow-up']" class="ml-2 transform rotate-45" />
            </button>
          </div>
          <div class="mt-3 md:mt-0">
            <div class="flex flex-col justify-center ml-auto leading-tight text-right">
              <div class="text-xs tracking-wider text-gray-500 uppercase">Total</div>
              <div class="text-sm font-bold">{{ $currency(amount / 100, account.currency) }}</div>
            </div>
          </div>
        </div>
        <!-- Empty -->
        <div v-if="records.meta.total < 1 && !$route.query.filter" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No records added yet.
          </div>
          <div class="text-gray-500">
            Records are flexible entries that can be any expense, purchase, or other record you'd like to keep.
          </div>
          <router-link :to="{ name: 'RecordCreate' }" class="mt-4 btn btn-outline">
            Add Record
          </router-link>
        </div>
        <!-- No Results -->
        <div v-else-if="records.meta.total < 1 && $route.query.filter" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No records matching your filters.
          </div>
          <div class="text-gray-500">
            Try changing your filter settings, or clear the filter to see latest records.
          </div>
          <button class="mt-4 btn btn-outline" @click="clearFilter">
            Clear Filters
          </button>
        </div>
        <!-- Table -->
        <div v-else class="flex flex-col overflow-hidden">
          <div class="py-2 -my-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div class="inline-block min-w-full overflow-hidden align-middle rounded-t-lg">
              <table class="min-w-full">
                <thead>
                  <tr>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b bg-gray-50">
                      Date
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b bg-gray-50">
                      Vendor
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b bg-gray-50">
                      Ref
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b bg-gray-50">
                      Vehicle
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b bg-gray-50">
                      Group
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b bg-gray-50">
                      Amount
                    </th>
                    <th class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b bg-gray-50">
                      Category
                    </th>
                    <th class="px-6 py-3 border-b bg-gray-50"></th>
                  </tr>
                </thead>
                <tbody class="bg-white">
                  <tr v-for="record in records.data" :key="record.id" class="hover:bg-gray-50">
                    <td class="px-6 py-4 border-b border-gray-100 whitespace-nowrap">
                      {{ record.date | moment('Do MMM YYYY') }}
                    </td>
                    <td class="px-6 py-4 font-bold border-b border-gray-100 whitespace-nowrap">
                      <span v-if="record.vendor">
                        {{ record.vendor.name }}
                      </span>
                    </td>
                    <td class="px-6 py-4 border-b border-gray-100 whitespace-nowrap">
                      {{ record.reference }}
                    </td>
                    <td class="px-6 py-4 border-b border-gray-100 whitespace-nowrap">
                      <span v-if="record.vehicle">
                        {{ record.vehicle.objectname }}
                      </span>
                    </td>
                    <td class="px-6 py-4 border-b border-gray-100 whitespace-nowrap">
                      <span v-if="record.vehicle_group">
                        {{ record.vehicle_group.name }}
                      </span>
                    </td>
                    <td class="px-6 py-4 border-b border-gray-100 whitespace-nowrap">
                      {{ $currency(record.amount / 100, account.currency) }}
                    </td>
                    <td class="px-6 py-4 border-b border-gray-100 whitespace-nowrap">
                      <span v-if="record.category">
                        {{ record.category.name }}
                      </span>
                    </td>
                    <td class="px-6 py-4 text-sm font-medium leading-5 text-right border-b border-gray-100 whitespace-nowrap">
                      <router-link :to="{ name: 'RecordEdit', params: { id: record.id } }" class="btn btn-secondary btn-sm">
                        Edit
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- Pagination -->
        <Pagination class="rounded-b-lg" :total="records.meta.total" :per-page="records.meta.per_page" :current-page="records.meta.current_page" />
      </div>
    </div>
  </div>
</template>

<script>
import { parse } from 'json2csv'
import { format, sub } from 'date-fns'
import { mapGetters, mapState } from 'vuex'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import VendorSelect from '@/components/VendorSelect'
import CategorySelect from '@/components/CategorySelect'
import VehicleSelect from '@/components/VehicleSelect'
import GroupSelect from '@/components/GroupSelect'
import FilterDropdown from '@/components/FilterDropdown'
import SortDropdown from '@/components/SortDropdown'

export default {
  name: 'RecordList',

  components: {
    DatePicker,
    VendorSelect,
    CategorySelect,
    VehicleSelect,
    GroupSelect,
    FilterDropdown,
    SortDropdown
  },

  created () {
    this.setFilter()
    this.getRecords()
  },

  computed: {
    ...mapState(['user']),
    ...mapGetters(['account'])
  },

  data () {
    return {
      loading: true,
      records: {},
      amount: 0,
      filter: {
        date: { start: sub(new Date(), { years: 1 }), end: new Date() },
        vendor: null,
        category: null,
        vehicle: null
      },
      sortOptions: [
        { label: `Date <span class="text-gray-500">(Oldest First)</span>`, prop: 'date', dir: 'asc' },
        { label: `Date <span class="text-gray-500">(Newest First)</span>`, prop: 'date', dir: 'desc' },
        { label: `Amount <span class="text-gray-500">(Highest First)</span>`, prop: 'amount', dir: 'desc' },
        { label: `Amount <span class="text-gray-500">(Lowest First)</span>`, prop: 'amount', dir: 'asc' },
        { label: `Vendor <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'vendor_id', dir: 'asc' },
        { label: `Vendor <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'vendor_id', dir: 'desc' },
        { label: `Reference <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'reference', dir: 'asc' },
        { label: `Reference <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'reference', dir: 'desc' },
        { label: `Vehicle <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'vehicle_id', dir: 'asc' },
        { label: `Vehicle <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'vehicle_id', dir: 'desc' }
      ]
    }
  },

  methods: {
    async getRecords () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/records`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'date',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.records = data.records
        this.amount = data.amount
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    async exportRecords (type) {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/records/export`, {
          params: {
            sort: this.$route.query.sort || 'date',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })

        const csv = parse(data.items)
        this.$download(csv, `Records_${this.filter.date.start}_${this.filter.date.end}.csv`)
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      }
    },

    async pullReport (type) {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/records/report`, {
          params: {
            sort: this.$route.query.sort || 'date',
            dir: this.$route.query.dir || 'desc',
            type: type,
            ...this.filter && { filter: this.filter }
          }
        })
      } catch (error) {
        console.error(error)
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        date: { start: sub(new Date(), { years: 1 }), end: new Date() },
        vendor: null,
        category: null,
        vehicle: null
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    }
  },

  watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getRecords()
      },
      deep: true
    }
  }
}
</script>